import apiClient from './axiosClient';

// ─────────────────────────────────────────────────────────────────────────────
// Session helpers
// ─────────────────────────────────────────────────────────────────────────────
function getSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem('ar_user') || '{}');
  } catch {
    return {};
  }
}
const getReviewerEmail = () => getSessionUser().userId || '';

// ─────────────────────────────────────────────────────────────────────────────
// Response normalizers  (API shape → UI shape)
// ─────────────────────────────────────────────────────────────────────────────

/** Map a single application from the API envelope to the shape the UI uses. */
function normalizeApp(apiApp) {
  return {
    appId: apiApp.appId,
    appName: apiApp.appName,
    appOwner: apiApp.appOwner ?? '',
    appLeader: apiApp.appLeader ?? '',
    appAccess: (apiApp.accessGroups ?? []).join(', ') || '—',
    description: apiApp.entityId ? `Entity: ${apiApp.entityId}` : '',
    accessGroups: apiApp.accessGroups ?? [],
    isSignedOff: apiApp.isSignedOff ?? false,
    signoffBy: apiApp.signoffBy ?? null,
    signoffAt: apiApp.signoffAt ?? null,
    totalUsers: apiApp.totalUsers ?? 0,
    retained: apiApp.approved ?? 0,
    revoked: apiApp.rejected ?? 0,
    pending: apiApp.pending ?? 0,
    totalAuthEvents: apiApp.totalAuthEvents ?? null,
    accessPeriod: apiApp.accessPeriod ?? apiApp.access_period ?? null,
    dueDate: apiApp.dueDate ?? apiApp.due_date ?? null,
  };
}

/** Map an access record from the API to the shape the grid expects. */
function normalizeRecord(rec) {
  return {
    recordId: rec.recordId,
    appId: rec.appId,
    userId: rec.userId,
    fullUsername: rec.fullUsername,
    userEmail: rec.userEmail,
    title: rec.title,
    primaryBrand: rec.primaryBrand,
    multiBrandAssignment: rec.multiBrandAssignment,
    primaryJobRoleCode: rec.primaryJobRoleCode,
    jobDescription: rec.jobDescription,
    workCountry: rec.workCountry,
    businessUnitName: rec.businessUnitName,
    department: rec.department,
    businessMarketUnit: rec.businessMarketUnit ?? '',
    manager: rec.manager,
    managerEmail: rec.managerEmail,
    employmentStatus: rec.employmentStatus,
    lastAccess: rec.lastAccess ?? '',   // kept as raw ISO string; formatted at render time
    loginCount: rec.loginCount,
    accessStatus: rec.accessStatus,
    userAccessGroup: rec.userAccessGroup,
    reviewStatus: rec.reviewAction ?? 'Pending',
    reviewAction: rec.reviewAction ?? null,
    reviewComment: rec.reviewComment ?? '',
    reviewedBy: rec.reviewedBy ?? '',
    reviewedAt: rec.reviewedAt ?? '',
  };
}

function normalizeReviewer(reviewer) {
  return {
    userId: reviewer.userid ?? '',
    name: reviewer.fullName ?? '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. GET /applications?ownerId={ownerId}
// ─────────────────────────────────────────────────────────────────────────────
export async function getApplicationsByOwner(userId) {
  const res = await apiClient.get('/applications');
  return (res.data?.data ?? []).map(normalizeApp);
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. GET /applications/{appId}/summary
// ─────────────────────────────────────────────────────────────────────────────
export async function getApplicationSummary(appId) {
  const res = await apiClient.get(`/applications/${appId}/summary`);
  return normalizeApp(res.data?.data ?? {});
}

export async function getReviewerOptions(appId) {
  const res = await apiClient.get(`/applications/${appId}/reviewers`);
  return (res.data?.data ?? []).map(normalizeReviewer);
}

export async function addReviewerToApplication(appId, userId, updatedBy) {
  const res = await apiClient.post(`/applications/${appId}/reviewers`, {
    appid: appId,
    userid: userId
  });
  return res.data?.data ?? {};
}

// ─────────────────────────────────────────────────────────────────────────────
// GET /applications/{appId}/owners
// ─────────────────────────────────────────────────────────────────────────────
export async function getApplicationOwners(appId) {
  const res = await apiClient.get(`/applications/${appId}/owners`);
  return (res.data?.data ?? []).map((row) => ({
    userid:    row.userid,
    fullName:  row.fullName,
    email:     row.email,
    role:      row.role,
    addedBy:   row.addedBy   ?? row.createdBy   ?? null,
    addedAt:   row.addedAt   ?? row.createdAt   ?? null,
    addedByName: row.addedByName ?? row.createdByName ?? null,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE /applications/{appId}/owners/{ownerUserId}
// ─────────────────────────────────────────────────────────────────────────────
export async function removeApplicationOwner(appId, ownerUserId) {
  const res = await apiClient.delete(`/applications/${appId}/owners/${ownerUserId}`);
  return res.data?.data ?? {};
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. GET /applications/{appId}/access-records  (paginated, parallel fetch)
//
//    Strategy:
//    ① First call  → page=1 pageSize=500 → gets first batch + totalRecords
//    ② Calculate remaining pages needed
//    ③ Fire remaining pages in parallel batches of 10
//    ④ Return combined normalized records
//
//    onProgress(loadedSoFar, total) is called after each batch so the UI can
//    show a progress indicator if desired.
// ─────────────────────────────────────────────────────────────────────────────
const PAGE_SIZE = 500;
const BATCH_SIZE = 10;     // max parallel calls per batch

async function fetchPage(appId, page) {
  const res = await apiClient.get(`/applications/${appId}/access-records`, {
    params: { page, pageSize: PAGE_SIZE },
  });
  const payload = res.data?.data ?? {};
  return {
    records: (payload.records ?? []).map(normalizeRecord),
    totalRecords: payload.totalRecords ?? 0,
    totalPages: payload.totalPages ?? 1,
  };
}

export async function getGridData(appId, onProgress) {
  // ① First page — also tells us total count
  const first = await fetchPage(appId, 1);
  const allRecords = [...first.records];
  const { totalRecords, totalPages } = first;

  if (onProgress) onProgress(allRecords.length, totalRecords);

  // If everything fits in one page, return immediately
  if (totalPages <= 1) return allRecords;

  // ② Build list of remaining pages  [2, 3, 4, …, totalPages]
  const remaining = [];
  for (let p = 2; p <= totalPages; p++) remaining.push(p);

  // ③ Fetch remaining pages in parallel batches of BATCH_SIZE
  for (let i = 0; i < remaining.length; i += BATCH_SIZE) {
    const batch = remaining.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(batch.map((p) => fetchPage(appId, p)));
    results.forEach((r) => allRecords.push(...r.records));
    if (onProgress) onProgress(allRecords.length, totalRecords);
  }

  return allRecords;
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. PATCH /applications/{appId}/access-records/{recordId}/action
//    Body: { userid, status, comment, reviewedBy, reviewedAt }
// ─────────────────────────────────────────────────────────────────────────────
export async function updateAction(appId, recordId, userId, status, comment = '') {
  const res = await apiClient.put(
    `/applications/${appId}/access-records/${recordId}/action`,
    {
      userid: userId,
      status,
      comment,
      reviewedAt: new Date().toISOString(),
    }
  );
  return res.data?.data ?? {};
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. PATCH /applications/{appId}/access-records/bulk-action
//    Body: { records: [{recordId, userid}], status, reviewedBy, reviewedAt }
// ─────────────────────────────────────────────────────────────────────────────
export async function bulkUpdateActions(appId, records, status) {
  const res = await apiClient.put(
    `/applications/${appId}/access-records/bulk-action`,
    {
      records: records.map((r) => ({ recordId: r.recordId, userid: r.userId })),
      status,
      reviewedAt: new Date().toISOString(),
    }
  );
  return res.data?.data ?? {};
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. POST /applications/{appId}/audit
//    Body: { reviewId, userid, action, comment, updatedBy, updatedAt }
// ─────────────────────────────────────────────────────────────────────────────
export async function addAuditData(appId, { action, recordId = null, userId = null, comment = '' }) {
  const res = await apiClient.post(`/applications/${appId}/audit`, {
    reviewId: recordId,
    userid: userId,
    action,
    comment,
    updatedAt: new Date().toISOString(),
  });
  return res.data?.data ?? {};
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. POST /applications/{appId}/sign-off
//    Body: { signedOffBy, signedOffAt }
// ─────────────────────────────────────────────────────────────────────────────
export async function updateFinalSignOff(appId) {
  const res = await apiClient.post(`/applications/${appId}/sign-off`, {
    signedOffAt: new Date().toISOString(),
  });
  return res.data?.data ?? {};
}

// ─────────────────────────────────────────────────────────────────────────────
// PM Dashboard — fires both APIs in parallel and merges the results:
//   GET /applications/pm-summary      → apps list with review stats
//   GET /applications/appownersreport → reviewer/owner assignments per app
// ─────────────────────────────────────────────────────────────────────────────
export async function getPMDashboardData() {
  const [summaryRes, reportRes] = await Promise.all([
    apiClient.get('/applications/pm-summary'),
    apiClient.get('/applications/appownersreport'),
  ]);

  // ── Apps from pm-summary ──────────────────────────────────────────────────
  const apps = (summaryRes.data?.data ?? []).map(normalizeApp);

  // ── Owners/reviewers from appownersreport ─────────────────────────────────
  // appOwner and appLeader on the main object identify non-reviewers
  // (values can be comma-separated email strings).
  // Collect those emails into a set, then exclude matching entries from
  // the owners array — what remains are the actual reviewers.
  const ownersMap = {};
  (reportRes.data?.data ?? []).forEach((row) => {
    if (!ownersMap[row.appId]) ownersMap[row.appId] = [];

    // Build exclusion set from appOwner + appLeader (comma-separated emails)
    const excludedEmails = new Set(
      [row.appOwner ?? '', row.appLeader ?? '']
        .join(',')
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    );

    (row.owners ?? []).forEach((r) => {
      const email = (r.email ?? '').trim().toLowerCase();
      // Skip if this person is the app owner or app leader
      if (email && excludedEmails.has(email)) return;
      ownersMap[row.appId].push({
        userid:     r.userid     ?? '',
        fullName:   r.fullName   ?? '',
        email:      r.email      ?? '',
        title:      r.title      ?? '',
        department: r.department ?? '',
      });
    });
  });

  return { apps, ownersMap };
}

// ─────────────────────────────────────────────────────────────────────────────
// Backward-compat aliases used by existing components
// ─────────────────────────────────────────────────────────────────────────────
export const getUserAccessList = getGridData;
export const finalSignOff = updateFinalSignOff;
export const updateUserReviewStatus = (appId, recordId, userId, status, comment) =>
  updateAction(appId, recordId, userId, status, comment);
export const bulkUpdateReviewStatus = (appId, records, status) =>
  bulkUpdateActions(appId, records, status);
